import {skipToken} from '@reduxjs/toolkit/query';
import {FlagProps, Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetInterestV2Query} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {interestRoutes} from '@dms/routes';
import {getCustomerName, getInterestStateFlagProps, getInterestTypeFlagProps} from '@dms/shared';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';

const LOADING_PLACEHOLDER = '…';

export function useInterestPairedRecord(interestId: string | null): PairedRecordView {
  const {
    data: interest,
    isLoading,
    isError,
  } = useGetInterestV2Query(isNotNil(interestId) ? {id: interestId} : skipToken);

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(interest?.customerId) ? {customerId: interest.customerId} : skipToken
  );

  const formatDateTime = useDateTimeFormatter();

  const code =
    interest?.publicId ??
    (isLoading ? LOADING_PLACEHOLDER : i18n.t('general.notifications.notFound'));
  const customerName = getCustomerName(customer);
  const createdAtFormatted = interest?.createdAt
    ? formatDateTime('dateShort', parseDate(interest.createdAt))
    : null;
  const updatedAtFormatted = interest?.updatedAt
    ? formatDateTime('dateShort', parseDate(interest.updatedAt))
    : null;

  const stateFlag = getInterestStateFlagProps(interest?.state);
  const typeFlag = getInterestTypeFlagProps(interest?.type);

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(isNotNil(createdAtFormatted), `${i18n.t('general.labels.created')} ${createdAtFormatted}`)
    .when(
      isNotNil(updatedAtFormatted),
      `${i18n.t('general.labels.lastUpdated')} ${updatedAtFormatted}`
    );

  const flags = [stateFlag, typeFlag].filter((flag): flag is FlagProps => isNotNil(flag));

  return {
    isLoading,
    isError,
    code,
    detailPath: isNotNil(interestId)
      ? composePath(interestRoutes.detailOverview, {params: {id: interestId}})
      : null,
    parameters,
    flags,
    vehicleIds: [],
  };
}
